/* ============================================================
   SpeedFly V6 — Global JavaScript
   Deferred, no jQuery dependency
   IntersectionObserver for lazy sections, drawer, modal, accordion, tabs
   ============================================================ */

(function () {
  'use strict';

  /* === TRAP FOCUS (Accessibility) === */
  const trapFocus = (container) => {
    const focusable = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    container.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  };

  /* === DRAWER === */
  class DrawerComponent {
    constructor() {
      this.overlay = document.querySelector('.drawer-overlay');
      this.drawer = document.querySelector('.drawer');
      if (!this.overlay || !this.drawer) return;

      this.closeBtn = this.drawer.querySelector('.drawer__close');
      this.bindEvents();
    }

    bindEvents() {
      document.querySelectorAll('[data-drawer-open]').forEach((btn) => {
        btn.addEventListener('click', () => this.open());
      });

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      this.overlay.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
    }

    open() {
      this.overlay.classList.add('is-active');
      this.drawer.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      trapFocus(this.drawer);
      this.drawer.querySelector('.drawer__close')?.focus();
    }

    close() {
      this.overlay.classList.remove('is-active');
      this.drawer.classList.remove('is-active');
      document.body.style.overflow = '';
    }

    isOpen() {
      return this.drawer.classList.contains('is-active');
    }
  }

  /* === MODAL === */
  class ModalComponent {
    constructor() {
      this.bindEvents();
    }

    bindEvents() {
      document.querySelectorAll('[data-modal-open]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const targetId = btn.getAttribute('data-modal-open');
          const overlay = document.getElementById(targetId);
          if (overlay) this.open(overlay);
        });
      });

      document.querySelectorAll('[data-modal-close]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const overlay = btn.closest('.modal-overlay');
          if (overlay) this.close(overlay);
        });
      });

      document.querySelectorAll('.modal-overlay').forEach((overlay) => {
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) this.close(overlay);
        });
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const active = document.querySelector('.modal-overlay.is-active');
          if (active) this.close(active);
        }
      });
    }

    open(overlay) {
      overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      const modal = overlay.querySelector('.modal');
      if (modal) trapFocus(modal);
    }

    close(overlay) {
      overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    }
  }

  /* === ACCORDION === */
  class AccordionComponent {
    constructor() {
      document.querySelectorAll('.accordion__trigger').forEach((trigger) => {
        trigger.addEventListener('click', () => this.toggle(trigger));
      });
    }

    toggle(trigger) {
      const content = trigger.nextElementSibling;
      if (!content) return;

      const isOpen = trigger.getAttribute('aria-expanded') === 'true';

      // Close siblings if within same accordion
      const accordion = trigger.closest('.accordion');
      if (accordion && !accordion.hasAttribute('data-allow-multiple')) {
        accordion.querySelectorAll('.accordion__trigger').forEach((t) => {
          t.setAttribute('aria-expanded', 'false');
          if (t.nextElementSibling) {
            t.nextElementSibling.classList.remove('is-active');
          }
        });
      }

      trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      content.classList.toggle('is-active', !isOpen);
    }
  }

  /* === TABS === */
  class TabsComponent {
    constructor() {
      document.querySelectorAll('.tabs').forEach((tabs) => {
        this.init(tabs);
      });
    }

    init(container) {
      const triggers = container.querySelectorAll('.tabs__trigger');
      const panels = container.querySelectorAll('.tabs__panel');

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const targetId = trigger.getAttribute('aria-controls');

          triggers.forEach((t) => t.setAttribute('aria-selected', 'false'));
          panels.forEach((p) => p.classList.remove('is-active'));

          trigger.setAttribute('aria-selected', 'true');
          const target = document.getElementById(targetId);
          if (target) target.classList.add('is-active');
        });
      });
    }
  }

  /* === LAZY SECTION LOADING (IntersectionObserver) === */
  class LazySections {
    constructor() {
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('section-loaded');
              this.observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '200px 0px', threshold: 0 }
      );

      document.querySelectorAll('[data-lazy-section]').forEach((section) => {
        this.observer.observe(section);
      });
    }
  }

  /* === SECTION REVEAL ANIMATIONS (IntersectionObserver) === */
  class SectionReveal {
    constructor() {
      if (!('IntersectionObserver' in window)) return;

      /* Respect prefers-reduced-motion */
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced) {
        document.querySelectorAll('[data-section-animation]').forEach((el) => {
          el.classList.add('is-revealed');
        });
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              this.observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -60px 0px', threshold: 0.1 }
      );

      document.querySelectorAll('[data-section-animation]').forEach((section) => {
        this.observer.observe(section);
      });
    }
  }

  /* === QUANTITY SELECTOR === */
  class QuantitySelector {
    constructor() {
      document.querySelectorAll('.quantity-selector').forEach((selector) => {
        this.init(selector);
      });
    }

    init(selector) {
      const input = selector.querySelector('.quantity-selector__input');
      const minus = selector.querySelector('[data-qty-minus]');
      const plus = selector.querySelector('[data-qty-plus]');
      if (!input) return;

      const min = parseInt(input.getAttribute('min') || '1', 10);
      const max = parseInt(input.getAttribute('max') || '999', 10);

      if (minus) {
        minus.addEventListener('click', () => {
          const val = parseInt(input.value, 10);
          if (val > min) {
            input.value = val - 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      if (plus) {
        plus.addEventListener('click', () => {
          const val = parseInt(input.value, 10);
          if (val < max) {
            input.value = val + 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      input.addEventListener('change', () => {
        let val = parseInt(input.value, 10);
        if (isNaN(val) || val < min) val = min;
        if (val > max) val = max;
        input.value = val;
      });
    }
  }

  /* === CART API HELPERS === */
  window.SpeedFly = window.SpeedFly || {};

  SpeedFly.cart = {
    async get() {
      const res = await fetch(window.routes.cart_url + '.js');
      return res.json();
    },

    async add(items) {
      const res = await fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || err.message);
      }
      return res.json();
    },

    async change(line, quantity) {
      const res = await fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      return res.json();
    },

    async update(updates) {
      const res = await fetch(window.routes.cart_update_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      return res.json();
    },
  };

  /* === PUBLISH CUSTOM EVENTS === */
  SpeedFly.publish = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`speedfly:${name}`, { detail, bubbles: true }));
  };

  SpeedFly.subscribe = (name, callback) => {
    document.addEventListener(`speedfly:${name}`, callback);
  };

  /* === CART COUNT UPDATE === */
  SpeedFly.updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  };

  /* === LIVE REGION UPDATE === */
  SpeedFly.announce = (message) => {
    const region = document.getElementById('cart-live-region');
    if (region) region.textContent = message;
  };

  /* === DEBOUNCE UTILITY === */
  SpeedFly.debounce = (fn, delay = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), delay);
    };
  };

  /* === CARD SWATCH HOVER — image preview on collection cards === */
  class CardSwatchHover {
    constructor() {
      this.handlers = [];
      this.init();
    }

    init() {
      document.querySelectorAll('[data-card-swatches]').forEach((container) => {
        const card = container.closest('.product-card');
        if (!card) return;
        const mainImg = card.querySelector('.product-card__image:not(.product-card__image--hover)');
        if (!mainImg) return;

        /* Store original src for reset */
        const originalSrc = mainImg.currentSrc || mainImg.src;
        const originalSrcset = mainImg.getAttribute('srcset') || '';

        container.querySelectorAll('[data-card-swatch-trigger]').forEach((trigger) => {
          const swatchImg = trigger.dataset.swatchImage;
          if (!swatchImg) return;

          const onEnter = () => {
            mainImg.setAttribute('srcset', swatchImg);
            mainImg.src = swatchImg;
          };
          const onLeave = () => {
            if (originalSrcset) {
              mainImg.setAttribute('srcset', originalSrcset);
            }
            mainImg.src = originalSrc;
          };

          trigger.addEventListener('mouseenter', onEnter);
          trigger.addEventListener('mouseleave', onLeave);
          this.handlers.push({ el: trigger, onEnter, onLeave });
        });
      });
    }

    destroy() {
      this.handlers.forEach(({ el, onEnter, onLeave }) => {
        el.removeEventListener('mouseenter', onEnter);
        el.removeEventListener('mouseleave', onLeave);
      });
      this.handlers = [];
    }
  }

  /* === INIT === */
  document.addEventListener('DOMContentLoaded', () => {
    new DrawerComponent();
    new ModalComponent();
    new AccordionComponent();
    new TabsComponent();
    new LazySections();
    new SectionReveal();
    new QuantitySelector();
    new CardSwatchHover();
  });
})();
